import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { RawMaterial } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import { getApiErrorMessage } from 'src/utils/errorHandler';

interface PaginatedMaterialResponse {
  data: RawMaterial[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetMaterial = () => {
  const [materials, setMaterials] = useState<RawMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchMaterials = async (
    page = 1,
    limit = 15,
    search = '',
    sortBy = 'name',
    order = 'ASC',
    categoryId?: string
  ) => {
    setLoading(true);
    try {
      const params: Record<string, string | number> = {
        offset: page,
        limit: limit,
        search: search,
        sortBy: sortBy,
        order: order,
      };

      if (categoryId) {
        params.categoryId = categoryId;
      }

      const response = await axiosTokenInstance.get<PaginatedMaterialResponse>(
        apiUrls.MATERIAL_MANAGEMENT,
        {
          params,
          withCredentials: true,
        }
      );

      setMaterials(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setMaterials([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteMaterial = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(
        apiUrls.DELETE_RAW_MATERIAL,
        {
          params: { id },
        }
      );
      showSnackbar(response.data.message, 'success');
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
    }
  };

  return {
    loading,
    materials,
    totalItems,
    totalPages,
    currentPage,
    fetchMaterials,
    deleteMaterial,
  };
};

export const getBasicMaterials = async () => {
  const response = await axiosTokenInstance.get(apiUrls.MATERIAL_LIST_BASIC);
  return response.data.data;
};

export const useGetBasicMaterials = () => {
  const [basicMaterials, setBasicMaterials] = useState<any[]>([]);
  const { showSnackbar } = StyledSnackbar();

  const fetchBasicMaterials = async () => {
    try {
      const materialData = await getBasicMaterials();
      setBasicMaterials(materialData || []);
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      setBasicMaterials([]);
    }
  };

  return {
    basicMaterials,
    fetchBasicMaterials,
  };
};

export interface RawMaterialLog {
  id: string;
  rawMaterialId: string;
  rawMaterialName: string;
  beforeQuantity: number;
  changedQuantity: number;
  afterQuantity: number;
  logType: string;
  createdAt: string;
  logTypeReference: string;
}

export interface RawMaterialLogsResponse {
  data: RawMaterialLog[];
  totalItems: number;
}

export interface RawMaterialLogsParams {
  rawMaterialId: string;
  startCreatedDate?: string;
  endCreatedDate?: string;
  logType?: number;
  offset?: number;
  limit?: number;
  sortBy?: string;
  order?: 'ASC' | 'DESC';
}

export const fetchRawMaterialLogs = async (
  params: RawMaterialLogsParams
): Promise<RawMaterialLogsResponse> => {
  const queryParams: Record<string, string | number> = {
    rawMaterialId: params.rawMaterialId,
  };

  if (params.startCreatedDate) {
    queryParams.startCreatedDate = params.startCreatedDate;
  }
  if (params.endCreatedDate) {
    queryParams.endCreatedDate = params.endCreatedDate;
  }
  if (params.logType !== undefined) {
    queryParams.logType = params.logType;
  }
  if (params.offset !== undefined) {
    queryParams.offset = params.offset;
  }
  if (params.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params.sortBy) {
    queryParams.sortBy = params.sortBy;
  }
  if (params.order) {
    queryParams.order = params.order;
  }

  const response = await axiosTokenInstance.get<RawMaterialLogsResponse>(
    apiUrls.RAW_MATERIAL_LOGS,
    { params: queryParams }
  );
  return response.data;
};
